import { SurchargeMethodTerms } from "../../domain/invoicing/surcharges/surchargeTerms"
import { surchargeBlockedReason, surchargeCostText, surchargeMaximumText } from "../../domain/invoicing/surcharges/surchargeTermsModel"
import Logger from "../../logging/logger"

export type PaymentFeeRow = {
  key: 'card' | 'bank'
  method: string
  costText: string
  maximumText: string
  note: string | null
}

export type PaymentFeesView = {
  canTurnOn: boolean
  blockedReason: string | null
  rows: PaymentFeeRow[]
  cardMaximumText: string | null
  bankMaximumText: string | null
}

const toRow = (
  key: PaymentFeeRow['key'],
  method: string,
  terms: SurchargeMethodTerms,
  canTurnOn: boolean
): PaymentFeeRow | null => {
  const costText = surchargeCostText(terms)
  if (costText === null) {
    if (terms.allowed) {
      Logger.logError(new Error(`Pass On Payment Fees allowed a ${key} surcharge without a cost rate`))
    }
    return null
  }
  return {
    key,
    method,
    costText,
    maximumText: surchargeMaximumText(terms) ?? 'Not surcharged',
    note: !terms.allowed && canTurnOn ? terms.unavailableReason : null
  }
}

export const needsSurchargeDisclaimer = (isOn: boolean, disclaimerAccepted: boolean): boolean =>
  !isOn && !disclaimerAccepted

export const isTurnOnBlocked = (view: PaymentFeesView, isOn: boolean): boolean =>
  !isOn && !view.canTurnOn

export const toPaymentFeesView = (card: SurchargeMethodTerms, bank: SurchargeMethodTerms): PaymentFeesView => {
  const blockedReason = surchargeBlockedReason(card, bank)
  const canTurnOn = blockedReason === null
  return {
    canTurnOn,
    blockedReason,
    rows: [
      toRow('card', 'Credit card', card, canTurnOn),
      toRow('bank', 'Bank transfer', bank, canTurnOn)
    ].filter((row): row is PaymentFeeRow => row !== null),
    cardMaximumText: surchargeMaximumText(card),
    bankMaximumText: surchargeMaximumText(bank)
  }
}
